import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";

const SHIFT_OPTIONS = ["Opening", "General", "Closing", "Week Off", "Custom"];
const STATUS_OPTIONS = ["Present", "Absent", "Leave", "Week Off", "Holiday"];
const DEFAULT_TIMES = { Opening: "8 AM – 4 PM", General: "10 AM – 6 PM", Closing: "4 PM – 12 AM", "Week Off": "", Custom: "" };

function weekStart(d) { const t = new Date(d); t.setDate(t.getDate() - t.getDay() + (t.getDay() === 0 ? -6 : 1)); return t.toISOString().slice(0,10); }
function addDays(iso, n) { const t = new Date(iso); t.setDate(t.getDate()+n); return t.toISOString().slice(0,10); }
function dayName(iso) { return ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][new Date(iso).getDay()]; }
function prettyDate(iso) { const d = new Date(iso); return d.toLocaleDateString(undefined,{day:"numeric", month:"short"}); }

export default function DutyRoster({ open, onOpenChange, outlets, defaultOutletId }) {
  const today = new Date().toISOString().slice(0,10);
  const [outletId, setOutletId] = useState(defaultOutletId || "");
  const [wStart, setWStart] = useState(weekStart(today));
  const [staff, setStaff] = useState([]);
  const [entries, setEntries] = useState([]);
  useEffect(() => { if (defaultOutletId) setOutletId(defaultOutletId); }, [defaultOutletId]);
  useEffect(() => { if (!open) return;
    api.get("/staff").then(r => setStaff(r.data.filter(s => s.status === "Active")));
  }, [open]);
  const reload = async () => {
    if (!outletId) return;
    try { const r = await api.get(`/roster?outlet_id=${outletId}&week_start=${wStart}`); setEntries(r.data.entries); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { if (open && outletId) reload(); }, [open, outletId, wStart]);

  const days = useMemo(() => Array.from({length: 7}, (_, i) => addDays(wStart, i)), [wStart]);
  const byKey = useMemo(() => { const m = {}; for (const e of entries) m[`${e.staff_id}|${e.date}`] = e; return m; }, [entries]);
  const outletStaff = staff.filter(s => !outletId || s.outlet_id === outletId);

  const upsert = async (staff_id, date, patch) => {
    const cur = byKey[`${staff_id}|${date}`] || {};
    try {
      const body = { staff_id, outlet_id: outletId, date,
        shift: patch.shift ?? cur.shift ?? "General",
        duty_time: patch.duty_time ?? cur.duty_time ?? DEFAULT_TIMES[patch.shift ?? cur.shift ?? "General"],
        status: patch.status ?? cur.status ?? "Present",
        remarks: patch.remarks ?? cur.remarks ?? "" };
      await api.post("/roster/entry", body); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const buildWhatsApp = () => {
    const lines = ["*CHAI CHASKA CAFE*", "*STAFF DUTY ROSTER*",
      `Week: ${prettyDate(wStart)} – ${prettyDate(days[6])}`,
      outlets.find(o => o.id === outletId)?.name || "", ""];
    for (const d of days) {
      lines.push(`*${dayName(d).toUpperCase()}*`);
      const dayEntries = outletStaff.map(s => byKey[`${s.id}|${d}`]).filter(Boolean);
      if (dayEntries.length === 0) { lines.push("  —"); }
      else {
        for (const e of dayEntries) {
          const sName = staff.find(x => x.id === e.staff_id)?.full_name || "?";
          if (e.shift === "Week Off" || e.status === "Week Off") lines.push(`• ${sName} – Week Off`);
          else lines.push(`• ${sName} – ${e.shift}${e.duty_time ? ` – ${e.duty_time}` : ""}`);
        }
      }
      lines.push("");
    }
    return lines.join("\n").trim();
  };
  const copyWA = async () => {
    if (!outletId) return toast.error("Pick an outlet first");
    const txt = buildWhatsApp();
    try { await navigator.clipboard.writeText(txt); toast.success("Roster copied — paste in WhatsApp!"); }
    catch { toast.error("Clipboard blocked — long-press to copy manually"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-6xl max-h-[92vh] overflow-y-auto" style={{ color: "#2b1a0d" }} data-testid="roster-root">
        <DialogHeader><DialogTitle className="font-display text-2xl">Weekly Duty Roster</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-3 items-end mb-3 pb-3 border-b" style={{borderColor:"#F3E7C4"}}>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
            <select data-testid="roster-outlet" value={outletId} onChange={e => setOutletId(e.target.value)} className="h-9 px-2 rounded border bg-white min-w-[200px]" style={{borderColor:"#E4D6A8"}}>
              <option value="">— pick outlet —</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Week starting (Mon)</label>
            <input data-testid="roster-week" type="date" value={wStart} onChange={e => setWStart(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} />
          </div>
          <Button data-testid="roster-copy-wa" onClick={copyWA} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <Share2 size={14} className="mr-1" /> Copy for WhatsApp
          </Button>
        </div>
        {!outletId ? (
          <div className="rounded-2xl border border-dashed p-10 text-center text-sm" style={{borderColor:"#E4D6A8", color:"#7a6349"}}>Pick an outlet to see the roster.</div>
        ) : outletStaff.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-10 text-center text-sm" style={{borderColor:"#E4D6A8", color:"#7a6349"}}>No active staff assigned to this outlet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>
                  <th className="text-left py-2 sticky left-0 bg-white">Staff</th>
                  {days.map(d => <th key={d} className="text-left py-2 pl-2">{dayName(d).slice(0,3)} · {prettyDate(d)}</th>)}
                </tr>
              </thead>
              <tbody>
                {outletStaff.map(s => (
                  <tr key={s.id} className="border-t align-top" style={{borderColor:"#F3E7C4"}}>
                    <td className="py-2 pr-2 sticky left-0 bg-white font-medium min-w-[140px]" style={{color:"#2b1a0d"}}>{s.full_name}<div className="text-[10px] opacity-60">{s.designation}</div></td>
                    {days.map(d => {
                      const e = byKey[`${s.id}|${d}`] || {};
                      const isOff = e.shift === "Week Off" || e.status === "Week Off";
                      return (
                        <td key={d} className="py-2 pl-2 pr-2 min-w-[140px]">
                          <select data-testid={`rst-shift-${s.id}-${d}`} value={e.shift || "General"} onChange={ev => upsert(s.id, d, { shift: ev.target.value, duty_time: DEFAULT_TIMES[ev.target.value] || "" })}
                            className={`h-7 px-1 rounded border text-xs w-full ${isOff ? "bg-amber-50" : "bg-white"}`} style={{borderColor:"#E4D6A8"}}>
                            {SHIFT_OPTIONS.map(x => <option key={x}>{x}</option>)}
                          </select>
                          <input data-testid={`rst-time-${s.id}-${d}`} value={e.duty_time || ""} onChange={ev => upsert(s.id, d, { duty_time: ev.target.value })}
                            placeholder="time" className="h-6 mt-1 px-1 rounded border text-[11px] w-full bg-white" style={{borderColor:"#F3E7C4"}} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
